import {
	Component, Input, Output, OnInit, OnChanges, SimpleChanges, OnDestroy,
	EventEmitter, ChangeDetectorRef
} from '@angular/core';
import { Observable, Subject } from 'rxjs/Rx';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { IpAddress, Device } from 'DiscoveryEngine.TS/Core';
import {
	MonitorPolicy, MonitorSettings_Snmp, SnmpMonitorEntry
} from 'DiscoveryEngine.TS/Monitors';
import { List, ValueEncoder, Guid, ObjUtils } from 'DiscoveryEngine.TS/Utils';
import { IdentityService } from 'identity';
import { NavigationService, NetFacadeService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	GetDeviceResultDto, GetMonitorStateResultDto, MonitorStateDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { AgentMonitorPolicyDto } from 'UvExplorerServer.Dtos.TS';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';


export class CustomSnmpStateListEntry {
	public deviceGuid: string;
	public settingsEntryGuid: string;
	public deviceName: string;
	public ipAddress: string;
	public entryName: string;
	public state: string;
	public stateDetails: string;
	public style: string;

	public deviceTag: Device;
	public get hasDeviceTag(): boolean {
		return !!this.deviceTag;
	}

	public ipAddressTag: IpAddress;
	public get hasIpAddressTag(): boolean {
		return !!this.ipAddressTag;
	}

	public settingsEntryTag: SnmpMonitorEntry;
	public get hasSettingsEntryTag(): boolean {
		return !!this.settingsEntryTag;
	}

	constructor(deviceGuid?: string, device?: Device, settingsEntry?: SnmpMonitorEntry,
		settingsEntryState?: string, settingsEntryStateDetails?: string) {

		if (deviceGuid) {
			this.deviceGuid = deviceGuid;
			this.settingsEntryGuid = ObjUtils.GuidToString(settingsEntry.Guid);

			if (device) {
				this.deviceName = device.DisplayName;
			}
			else {
				this.deviceName = deviceGuid;
			}

			if (!!device && device.HasIpAddress) {
				this.ipAddress = device.IpAddress.ToString();
				this.ipAddressTag = device.IpAddress;
			}
			else {
				this.ipAddress = "";
				this.ipAddressTag = null;
			}

			if (settingsEntry.HasName) {
				this.entryName = settingsEntry.Name;
			}
			else {
				this.entryName = "";
			}

			if (settingsEntryState) {
				this.state = settingsEntryState;
				if (this.state === "NoData") {
					this.state = "No Data";
				}

				this.style = "monitor-state-" + settingsEntryState.toLowerCase();
			}
			else {
				this.state = "No Data";
				this.style = "monitor-state-nodata";
			}

			if (settingsEntryStateDetails) {
				this.stateDetails = settingsEntryStateDetails;
			}
			else {
				this.stateDetails = "";
			}

			this.deviceTag = device;
			this.settingsEntryTag = settingsEntry;
		}
		else {
			this.deviceGuid = "";
			this.settingsEntryGuid = "";
			this.deviceName = "";
			this.ipAddress = "";
			this.entryName = "";
			this.state = "No Data";
			this.style = "monitor-state-nodata";
			this.stateDetails = "";

			this.deviceTag = null;
			this.ipAddressTag = null;
			this.settingsEntryTag = null;
		}
	}
}

@Component({
	selector: 'custom-snmp-state',
	templateUrl: './customsnmpstate.component.html',
	styleUrls: ['./customsnmpstate.component.scss']
})
export class CustomSnmpStateComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public visible: boolean;

	@Input()
	public target: AgentMonitorPolicyDto;

	@Output()
	public visibleChange: EventEmitter<boolean>;

	@Output()
	public onClose: EventEmitter<any>;

	public entries: CustomSnmpStateListEntry[];
	//public rowGroupMetadata: any;

	private deviceCache: Object;
	private stateCache: Object;

	private destroyed: boolean;
	private eventsInterval: number;
	private timerId: any;

	constructor(private monService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private netFacadeService: NetFacadeService,
		private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {

		this.visible = false;
		this.target = null;
		this.visibleChange = new EventEmitter<boolean>();
		this.onClose = new EventEmitter<any>();

		this.entries = [];

		this.deviceCache = {};
		this.stateCache = {};

		this.destroyed = false;
		this.eventsInterval = 0;
		this.timerId = undefined;
	}

	public ngOnInit(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.destroyed)
			return;

		if (changes["visible"]) {
			this.entries = [];
			this.deviceCache = {};
			this.stateCache = {};

			this.stopTimer();
			if (this.visible && this.navService.isNetworkLoaded) {
				this.retrieveDevicesAndMonitorStates(true);
				this.startTimer();
			}
		}
		else {
			this.stopTimer();
		}
	}

	public ngOnDestroy(): void {
		this.stopTimer();
	}

	public get policyName(): string {
		if (!!this.target && !!this.target.MonitorPolicy) {
			return this.target.MonitorPolicy.Name;
		}
		else {
			return "";
		}
	}

	private startTimer(): void {

		this.setEventsInterval();

		if (!this.timerId && this.visible && this.eventsInterval > 0) {
			let timeoutMillis = this.eventsInterval * 1000;
			this.timerId = setInterval(() => {
				this.retrieveDevicesAndMonitorStates(false);
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	private setEventsInterval(): void {
		this.eventsInterval = 0;

		let netViewGuid = this.navService.location.netViewGuid;
		let network: NetViewDto = this.netFacadeService.findNetwork(netViewGuid);
		if (network) {
			this.eventsInterval = network.EventsInterval;
		}
		else {
			this.messageService.displayErrorMessage("Could not set events interval");
		}
	}

	public show(): void {
		this.visible = true;
		this.visibleChange.emit(true);

		this.changeDetector.markForCheck();
	}

	public hide(): void {
		this.visible = false;
		this.visibleChange.emit(false);

		this.changeDetector.markForCheck();
	}

	public onCloseClicked(): void {
		this.onClose.emit();
		this.hide();
	}

	private retrieveDevicesAndMonitorStates(showBusy: boolean): void {

		this.deviceCache = {};
		this.stateCache = {};

		if (!!this.target && !!this.target.MonitorPolicy) {
			let monPolicy: MonitorPolicy = this.target.MonitorPolicy;
			if (this.target.MonitorPolicy.DeviceGuids.Count > 0) {
				let deviceGuidStrs = new List<string>();
				monPolicy.DeviceGuids.ForEach((guid: Guid) => {
					let guidStr = ValueEncoder.EncodeGuid(guid);
					deviceGuidStrs.Add(guidStr);
				});

				this.getDevices(deviceGuidStrs, showBusy).subscribe(
					(gdResult: any) => {
						this.getMonitorState(monPolicy, showBusy).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					},
					(gdError) => {
						this.messageService.displayErrorMessage("Could not retrieve monitor device info");
					});
			}
			else {
				this.rebuildEntries();
			}
		}
	}

	private rebuildEntries(): void {

		this.entries = [];

		if (!!this.target && !!this.target.MonitorPolicy) {
			let monPolicy: MonitorPolicy = this.target.MonitorPolicy;
			let monSettings = (monPolicy.Settings as MonitorSettings_Snmp);

			monPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {

				let deviceGuidStr = ObjUtils.GuidToString(deviceGuid);

				let device: Device = null;
				if (this.deviceCache[deviceGuidStr]) {
					device = this.deviceCache[deviceGuidStr];
				}

				monSettings.GetEntries().ForEach((entry: SnmpMonitorEntry) => {
					if (entry.DeviceGuid.Equals(deviceGuid)) {
						let entryGuidStr = ValueEncoder.EncodeGuid(entry.Guid);

						let state: string = "";
						let stateDetails: string = "";

						let key = this.stateKey(entryGuidStr, deviceGuidStr);
						if (!!this.stateCache[key]) {
							let monState: MonitorStateDto = this.stateCache[key];
							state = monState.monitorSettingsEntryState;
							stateDetails = monState.monitorSettingsEntryStateDetails;
						}

						let listEntry = new CustomSnmpStateListEntry(deviceGuidStr, device, entry,
							state, stateDetails);
						this.entries.push(listEntry);
					}
				});
			});
		}
	}

	private getDevices(deviceGuids: List<string>, showBusy: boolean): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "monitor-network", deviceGuids, showBusy).subscribe(
				(result: GetDeviceResultDto) => {
					result.Devices.ForEach((value: Device) => {
						let deviceGuidStr = ValueEncoder.EncodeGuid(value.GUID);
						this.deviceCache[deviceGuidStr] = value;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}

	private getMonitorState(policy: MonitorPolicy, showBusy: boolean): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.monService.getMonitorState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, policy, showBusy).subscribe(
				(result: GetMonitorStateResultDto) => {
					result.entries.forEach((entry: MonitorStateDto) => {
						let key = this.stateKey(entry.monitorSettingsEntryGuid, entry.deviceGuid);
						this.stateCache[key] = entry;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}

	private stateKey(entryGuid: string | Guid, deviceGuid: string | Guid): string {

		return (ObjUtils.GuidToString(entryGuid) + ":" + ObjUtils.GuidToString(deviceGuid));
	}

	public refreshEntries(): void {
		this.retrieveDevicesAndMonitorStates(true);
	}

	public doCustomSort(event): void {
		let comparer = function (a: CustomSnmpStateListEntry, b: CustomSnmpStateListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].toString().localeCompare(b[event.field].toString());
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
